'use client'

import { useRef, useState } from 'react'
import { cn } from '@ui/lib/utils'
import { Icon } from '@ui/primitives/Icon'
import { Text } from '@ui/primitives/Text'
import { Button } from '@ui/primitives/Button'

interface FileInputProps {
  accept?: string
  multiple?: boolean
  maxSize?: number // in MB
  onChange?: (files: File[]) => void
  error?: string
  disabled?: boolean
  label?: string
  helperText?: string
  className?: string
}

export function FileInput({
  accept = '*',
  multiple = false,
  maxSize = 10,
  onChange,
  error,
  disabled = false,
  label,
  helperText,
  className,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const validateAndSetFiles = (files: FileList | null) => {
    if (!files) return

    const fileArray = Array.from(files)
    const validFiles = fileArray.filter((file) => {
      const sizeInMB = file.size / (1024 * 1024)
      return sizeInMB <= maxSize
    })

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles)
      onChange?.(validFiles)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (disabled) return
    validateAndSetFiles(e.dataTransfer.files)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    validateAndSetFiles(e.target.files)
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(newFiles)
    onChange?.(newFiles)
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer',
          dragActive && 'border-blue-500 bg-blue-50',
          error && 'border-red-500',
          !dragActive && !error && 'border-gray-300 hover:border-gray-400',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
        />

        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
            <Icon name="upload" size="md" className="text-gray-400" />
          </div>
          <div>
            <Text size="sm" className="font-medium">
              Click to upload or drag and drop
            </Text>
            <Text size="xs">
              {accept !== '*' && `${accept} `}
              {maxSize && `(Max ${maxSize}MB)`}
            </Text>
          </div>
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-3 space-y-2">
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-gray-50 rounded border"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Icon name="file" size="sm" />
                <Text size="sm" className="truncate">
                  {file.name}
                </Text>
                <Text size="xs">
                  ({(file.size / 1024).toFixed(1)} KB)
                </Text>
              </div>
              <Button
                type="button"
               
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  removeFile(index)
                }}
              >
                <Icon name="close" size="sm" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {helperText && !error && (
        <Text size="xs" className="mt-1">
          {helperText}
        </Text>
      )}

      {error && (
        <Text size="xs" className="mt-1 text-red-600">
          {error}
        </Text>
      )}
    </div>
  )
}
